import React from 'react';
import LgmButton from './LgmButton';

export type ProductSearchMode = 'internal' | 'fda_sources';

interface FdaSourceSearchToggleProps {
  mode: ProductSearchMode;
  onChange: (mode: ProductSearchMode) => void;
  /**
   * Disables the FDA Sources option (Internal stays always enabled) while
   * an advanced query is active. FDA Sources search is structurally
   * basic-search-only (Requirement 1) — the endpoint has no
   * `advancedQuery`-shaped param — so switching into it while an advanced
   * filter is applied would strand the user on a "Type a search term"
   * empty state with no visible input to type into (the basic search box
   * stays replaced by the collapsed filter summary). Switching is safe
   * again once the advanced filter is cleared back to basic.
   */
  fdaSourcesDisabled?: boolean;
}

/**
 * Internal / FDA Sources search-mode toggle for the Products page basic
 * search (docs/features/fda-source-product-search.md, Task FSS-006).
 *
 * Rendered via `UnifiedSearchBar`'s `basicSearchModeToggle` slot, which
 * renders it regardless of panel/collapsed state or searchMode so the
 * toggle stays visible even while an advanced filter is active -- see
 * that prop's own doc comment. This component is what enforces the
 * basic-search-only constraint for the FDA Sources option specifically,
 * via `fdaSourcesDisabled`.
 *
 * Session-only state — the caller (ProductsRowGroupingDataGrid) owns
 * `mode` as plain component state, never persisted to localStorage
 * (resolved Open Question 1: a page refresh always resets to Internal).
 */
const FdaSourceSearchToggle = ({
  mode,
  onChange,
  fdaSourcesDisabled = false,
}: FdaSourceSearchToggleProps) => {
  return (
    <div
      role="group"
      aria-label="Search mode"
      style={styles.container}
      data-testid="fda-source-search-toggle">
      <LgmButton
        type="button"
        variant={mode === 'internal' ? 'contained' : 'outlined'}
        color="primary"
        aria-pressed={mode === 'internal'}
        style={styles.leftButton}
        onClick={() => onChange('internal')}>
        Internal
      </LgmButton>
      <LgmButton
        type="button"
        variant={mode === 'fda_sources' ? 'contained' : 'outlined'}
        color="primary"
        aria-pressed={mode === 'fda_sources'}
        disabled={fdaSourcesDisabled}
        title={
          fdaSourcesDisabled
            ? 'Clear the active advanced filter to search FDA Sources'
            : undefined
        }
        style={styles.rightButton}
        onClick={() => onChange('fda_sources')}>
        FDA Sources
      </LgmButton>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    gap: '0.25rem',
    marginLeft: '0.5rem',
    marginRight: '0.5rem',
  },
  leftButton: {
    fontSize: '0.8125rem',
    whiteSpace: 'nowrap',
    textTransform: 'none',
    padding: '0.375rem 0.75rem',
  },
  rightButton: {
    fontSize: '0.8125rem',
    whiteSpace: 'nowrap',
    textTransform: 'none',
    padding: '0.375rem 0.75rem',
  },
};

export default FdaSourceSearchToggle;
